import { useRef, type TouchEvent } from 'react';

export interface SwipeHandlers {
  readonly onLeft?: (() => void) | undefined;
  readonly onRight?: (() => void) | undefined;
  /** Minimum horizontal travel in px before a swipe registers. Default 50. */
  readonly threshold?: number;
  /** When `true`, the gesture is ignored if it began inside a horizontal-
   *  scroll surface (e.g. the DayStrip). Pass a CSS selector instead via
   *  `ignoreSelector` for finer control. */
  readonly ignoreSelector?: string;
}

export interface SwipeBindings {
  readonly onTouchStart: (e: TouchEvent<HTMLElement>) => void;
  readonly onTouchEnd: (e: TouchEvent<HTMLElement>) => void;
  readonly onTouchCancel: (e: TouchEvent<HTMLElement>) => void;
}

/**
 * Lightweight horizontal swipe detector. Returns onTouchStart /
 * onTouchEnd / onTouchCancel handlers you spread onto the swipe surface.
 *
 * Filters out vertical scrolls: the gesture only counts as a swipe when
 * |dx| > threshold AND |dx| > 1.5 × |dy|. So a phone user scrolling the
 * page won't accidentally trigger a date change.
 *
 * `ignoreSelector` lets the caller opt-out of swipe handling for touches
 * that started inside a specific descendant — e.g. the DayStrip pill row,
 * which has its own horizontal scroll.
 */
export function useSwipe(handlers: SwipeHandlers): SwipeBindings {
  const startRef = useRef<{ x: number; y: number } | null>(null);
  const threshold = handlers.threshold ?? 50;
  const ignoreSelector = handlers.ignoreSelector;

  return {
    onTouchStart: (e: TouchEvent<HTMLElement>): void => {
      const t = e.touches[0];
      if (t === undefined) return;
      // Skip if the touch began inside an opted-out region.
      if (ignoreSelector !== undefined) {
        const target = t.target as Element | null;
        if (target !== null && typeof target.closest === 'function') {
          if (target.closest(ignoreSelector) !== null) {
            startRef.current = null;
            return;
          }
        }
      }
      startRef.current = { x: t.clientX, y: t.clientY };
    },
    onTouchEnd: (e: TouchEvent<HTMLElement>): void => {
      const start = startRef.current;
      const t = e.changedTouches[0];
      startRef.current = null;
      if (start === null || t === undefined) return;
      const dx = t.clientX - start.x;
      const dy = t.clientY - start.y;
      if (Math.abs(dx) < threshold) return;
      if (Math.abs(dx) < Math.abs(dy) * 1.5) return;
      if (dx < 0) handlers.onLeft?.();
      else handlers.onRight?.();
    },
    onTouchCancel: (): void => {
      startRef.current = null;
    },
  };
}
